import React, { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "../../components/ui";
import {
  ArrowLeft,
  Star,
  Zap,
  Shield,
  CheckCircle2,
  Loader2,
  AlertCircle,
  TrendingUp,
  Info,
  Truck,
  ShoppingCart,
} from "lucide-react";
import { cn } from "../../lib/utils";
import { getAssetById, Asset } from "../../services/asset.api";
import { calculateHealthScore } from "../../utils/healthScore";
import { quoteTradeIn, quoteEnergySavings } from "../../services/businessRules.api";
import { NewModel } from "./BrowseModels";

// ── Helpers ──────────────────────────────────────────────────────────────

function formatCurrency(n: number) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(n);
}

// Trade-in valuation and energy savings now come from the backend
// (`/api/v1/business-rules/...`). Local depreciation / kWh formulas removed.

function getAssetAge(asset: Asset): number {
  const year =
    asset.purchaseYear ||
    (asset.purchasedAt ? new Date(asset.purchasedAt).getFullYear() : null);
  if (!year) return 0;
  return new Date().getFullYear() - year;
}

function getModelImage(category: string, brand: string): string {
  const lowerCategory = category.toLowerCase();
  const lowerBrand = brand.toLowerCase();
  
  // Refrigerator
  if (lowerCategory.includes("refrigerator") || lowerCategory.includes("fridge")) {
    if (lowerBrand.includes("whirlpool")) return "https://images.unsplash.com/photo-1571175443880-49e1d25b2bc5?w=1200&q=90";
    if (lowerBrand.includes("lg")) return "https://images.unsplash.com/photo-1584568694244-14fbdf83bd30?w=1200&q=90";
    if (lowerBrand.includes("samsung")) return "https://images.unsplash.com/photo-1571175351217-b90f7c810eb3?w=1200&q=90";
    return "https://images.unsplash.com/photo-1571175443880-49e1d25b2bc5?w=1200&q=90";
  }
  
  // Washer / Washing Machine
  if (lowerCategory.includes("washer") || lowerCategory.includes("washing")) {
    if (lowerBrand.includes("maytag")) return "https://images.unsplash.com/photo-1626806787461-102c1bfaaea1?w=1200&q=90";
    if (lowerBrand.includes("lg")) return "https://images.unsplash.com/photo-1604335399105-a0c585fd81a1?w=1200&q=90";
    if (lowerBrand.includes("samsung")) return "https://images.unsplash.com/photo-1582735689369-4fe89db7114c?w=1200&q=90";
    return "https://images.unsplash.com/photo-1626806787461-102c1bfaaea1?w=1200&q=90";
  }
  
  // Dryer
  if (lowerCategory.includes("dryer")) {
    return "https://images.unsplash.com/photo-1610557892470-55d9e80c0bce?w=1200&q=90";
  }
  
  // Dishwasher
  if (lowerCategory.includes("dishwasher")) {
    return "https://images.unsplash.com/photo-1585659722983-3a675dabf23d?w=1200&q=90";
  }
  
  // Oven / Range / Stove
  if (lowerCategory.includes("oven") || lowerCategory.includes("range") || lowerCategory.includes("stove")) {
    return "https://images.unsplash.com/photo-1574269909862-7e1d70bb8078?w=1200&q=90";
  }
  
  // Air Conditioner / AC / HVAC
  if (lowerCategory.includes("air conditioner") || lowerCategory.includes("ac") || 
      lowerCategory.includes("hvac") || lowerCategory.includes("air conditioning")) {
    return "https://images.unsplash.com/photo-1631545804726-c7f66d9e1f6a?w=1200&q=90";
  }
  
  // Water Heater
  if (lowerCategory.includes("water heater") || lowerCategory.includes("heater")) {
    return "https://images.unsplash.com/photo-1581094794329-c8112a89af12?w=1200&q=90";
  }
  
  // Microwave
  if (lowerCategory.includes("microwave")) {
    return "https://images.unsplash.com/photo-1585659722983-3a675dabf23d?w=1200&q=90";
  }
  
  // TV / Television
  if (lowerCategory.includes("tv") || lowerCategory.includes("television")) {
    return "https://images.unsplash.com/photo-1593359677879-a4bb92f829e1?w=1200&q=90";
  }

  // Thermostat
  if (lowerCategory.includes("thermostat")) {
    return "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=1200&q=90";
  }

  // Default appliance
  return "https://images.unsplash.com/photo-1556909212-d5b604d0c90d?w=1200&q=90";
}

// Import catalog from BrowseModels
const MODEL_CATALOG: Record<string, NewModel[]> = {
  refrigerator: [
    {
      id: "ref-budget-1",
      brand: "Whirlpool",
      name: "36-in. French Door",
      type: "refrigerator",
      price: 1149,
      discountPrice: 999,
      discount: 13,
      rating: 4.3,
      badges: ["Best Seller"],
      features: [
        "25 cu. ft. capacity",
        "Accu-Chill™ temperature management",
        "LED interior lighting",
        "Ice maker included",
      ],
      monthlyPayment: 83,
      modelNumber: "WRF535SWHZ",
      category: "refrigerator",
      warrantyYears: 1,
      energyStarCertified: true,
      annualEnergyCostEst: 62,
      reviewCount: 2840,
      tier: "budget",
      tag: "Best Seller",
      rebateEligible: true,
      rebateAmount: 50,
    },
    {
      id: "ref-mid-1",
      brand: "LG",
      name: "InstaView™ Door-in-Door",
      type: "refrigerator",
      price: 1999,
      discountPrice: 1699,
      discount: 15,
      rating: 4.6,
      badges: ["Top Rated"],
      features: [
        "InstaView™ knock-twice window",
        "Door-in-Door® access",
        "Linear Compressor (10-yr warranty)",
        "Craft Ice™ maker",
      ],
      monthlyPayment: 142,
      modelNumber: "LRMVS3006S",
      category: "refrigerator",
      warrantyYears: 1,
      energyStarCertified: true,
      annualEnergyCostEst: 54,
      reviewCount: 5120,
      tier: "mid-range",
      tag: "Top Rated",
      rebateEligible: true,
      rebateAmount: 75,
    },
    {
      id: "ref-lg-french-1",
      brand: "LG",
      name: "French Door Refrigerator",
      type: "refrigerator",
      price: 1699,
      discountPrice: 1189,
      discount: 30,
      rating: 4.6,
      badges: ["30% Off"],
      features: [
        "28 cu. ft. SmartInstaView™",
        "Door-in-Door® access",
        "Craft Ice™ maker",
        "Free delivery & haul-away",
      ],
      monthlyPayment: 99,
      modelNumber: "LRFXS2503S",
      category: "refrigerator",
      warrantyYears: 1,
      energyStarCertified: true,
      annualEnergyCostEst: 52,
      reviewCount: 4820,
      tier: "mid-range",
      tag: "30% Off",
      rebateEligible: false,
    },
    {
      id: "ref-premium-1",
      brand: "Samsung",
      name: "Bespoke 4-Door Flex",
      type: "refrigerator",
      price: 2499,
      discountPrice: 2499,
      discount: 0,
      rating: 4.7,
      badges: [],
      features: [
        "Customizable color panels",
        "FlexZone™ convertible drawer",
        "AI Energy Mode",
        "Family Hub™ smart display",
      ],
      monthlyPayment: 208,
    },
  ],
  washer: [
    {
      id: "wash-budget-1",
      brand: "Maytag",
      name: "Top Load Washer",
      type: "washer",
      price: 799,
      discountPrice: 649,
      discount: 19,
      rating: 4.2,
      badges: ["Best Value"],
      features: [
        "4.7 cu. ft. capacity",
        "Deep Fill option",
        "PowerWash® agitator",
        "12 wash cycles",
      ],
      monthlyPayment: 54,
      modelNumber: "MVW4505MW",
      category: "washer",
      warrantyYears: 1,
      energyStarCertified: true,
      annualEnergyCostEst: 34,
      reviewCount: 3400,
      tier: "budget",
      tag: "Best Value",
    },
    {
      id: "wash-whirlpool-1",
      brand: "Whirlpool",
      name: "Washer + Dryer Bundle",
      type: "washer",
      price: 1199,
      discountPrice: 849,
      discount: 29,
      rating: 4.5,
      badges: ["Bundle Deal"],
      features: [
        "Bundle & save $350",
        "High-efficiency top-load washer",
        "7.0 cu. ft. matching dryer",
        "Free delivery this weekend",
      ],
      monthlyPayment: 71,
      modelNumber: "WTW5000DW-WED5000DW",
      category: "washer",
      warrantyYears: 1,
      energyStarCertified: true,
      annualEnergyCostEst: 30,
      reviewCount: 6100,
      tier: "mid-range",
      tag: "Bundle Deal",
      rebateEligible: true,
      rebateAmount: 350,
    },
    {
      id: "wash-mid-1",
      brand: "LG",
      name: "TurboWash™ 360°",
      type: "washer",
      price: 1049,
      discountPrice: 899,
      discount: 14,
      rating: 4.5,
      badges: ["Top Rated"],
      features: [
        "TurboWash™ 360 in 30 min",
        "Steam technology",
        "AI DD™ optimal washing",
        "ThinQ® smart app control",
      ],
      monthlyPayment: 75,
      modelNumber: "WM4000HWA",
      category: "washer",
      warrantyYears: 1,
      energyStarCertified: true,
      annualEnergyCostEst: 26,
      reviewCount: 7200,
      tier: "mid-range",
      tag: "Top Rated",
      rebateEligible: true,
      rebateAmount: 50,
    },
    {
      id: "wash-premium-1",
      brand: "Samsung",
      name: "Super Speed Wash",
      type: "washer",
      price: 1199,
      discountPrice: 1199,
      discount: 0,
      rating: 4.6,
      badges: [],
      features: [
        "5.3 cu. ft. Ultra Capacity",
        "Super Speed Wash in 28 min",
        "AI SmartDial™",
        "Vibration Reduction Technology+",
      ],
      monthlyPayment: 100,
    },
  ],
  dishwasher: [
    {
      id: "dish-bosch-1",
      brand: "Bosch",
      name: "800 Series Super-Silent Dishwasher",
      type: "dishwasher",
      price: 799,
      discountPrice: 599,
      discount: 25,
      rating: 4.8,
      badges: ["$200 Rebate"],
      features: [
        "44 dBA ultra-quiet operation",
        "Stainless steel tub",
        "CrystalDry™ technology",
        "18 flexible wash programs",
      ],
      monthlyPayment: 50,
      modelNumber: "SHPM88Z75N",
      category: "dishwasher",
      warrantyYears: 1,
      energyStarCertified: true,
      annualEnergyCostEst: 28,
      reviewCount: 4120,
      tier: "premium",
      tag: "$200 Rebate",
      rebateEligible: true,
      rebateAmount: 200,
    },
  ],
  tv: [
    {
      id: "tv-samsung-1",
      brand: "Samsung",
      name: '65" 4K QLED TV',
      type: "tv",
      price: 1299,
      discountPrice: 899,
      discount: 31,
      rating: 4.7,
      badges: ["Save $400"],
      features: [
        "65-inch QLED 4K display",
        "120Hz refresh rate",
        "Quantum HDR+ picture quality",
        "SmartThings & Alexa built-in",
      ],
      monthlyPayment: 75,
      modelNumber: "QN65Q80CAFXZA",
      category: "tv",
      warrantyYears: 1,
      energyStarCertified: true,
      annualEnergyCostEst: 18,
      reviewCount: 3240,
      tier: "premium",
      tag: "Save $400",
      rebateEligible: false,
    },
  ],
  thermostat: [
    {
      id: "thermo-nest-1",
      brand: "Google Nest",
      name: "Learning Thermostat (4th Gen)",
      type: "thermostat",
      price: 249,
      discountPrice: 199,
      discount: 20,
      rating: 4.8,
      badges: ["$50 Off"],
      features: [
        "Auto-schedule learns your routine",
        "Remote control via app",
        "Energy history & savings reports",
        "Works with Alexa, Google & Apple",
      ],
      monthlyPayment: 17,
      modelNumber: "T4001ES",
      category: "thermostat",
      warrantyYears: 2,
      energyStarCertified: true,
      annualEnergyCostEst: 0,
      reviewCount: 12400,
      tier: "premium",
      tag: "$50 Off",
      rebateEligible: false,
    },
  ],
  default: [
    {
      id: "gen-mid-1",
      brand: "LG",
      name: "ThinQ Premium Series",
      type: "appliance",
      price: 1299,
      discountPrice: 1299,
      discount: 0,
      rating: 4.6,
      badges: [],
      features: [
        "ThinQ® AI technology",
        "Predictive Diagnostics™",
        "Streamlined premium finish",
        "Proactive service alerts",
      ],
      monthlyPayment: 108,
      modelNumber: "LG-PRE-2025",
      category: "appliance",
      reviewCount: 2400,
      energyStarCertified: true,
      annualEnergyCostEst: 30,
      warrantyYears: 1,
      tier: "premium",
    },
  ],
};

function findModelById(modelId: string): NewModel | null {
  for (const category of Object.values(MODEL_CATALOG)) {
    const model = category.find((m) => m.id === modelId);
    if (model) return model;
  }
  return null;
}

// ── Main Component ───────────────────────────────────────────────────────

export function ModelDetail() {
  const { t } = useTranslation('upgrade');
  const { id, modelId } = useParams<{ id: string; modelId: string }>();
  const navigate = useNavigate();

  const [asset, setAsset] = useState<Asset | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Server-quoted values (replace local trade-in & energy formulas)
  const [tradeInValue, setTradeInValue] = useState(0);
  const [energySavingsPerYear, setEnergySavingsPerYear] = useState(0);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    getAssetById(id)
      .then((data) => setAsset(data))
      .catch((err: unknown) => setError(err instanceof Error ? err.message : t('browse.loadError')))
      .finally(() => setLoading(false));
  }, [id]);

  // Fetch backend quotes once asset loads.
  useEffect(() => {
    if (!asset) return;
    let cancelled = false;

    const purchasePrice = asset.purchasePrice ?? 0;
    if (purchasePrice > 0) {
      const score = calculateHealthScore(asset);
      const condition: 'excellent' | 'good' | 'fair' | 'poor' =
        score >= 8 ? 'excellent' : score >= 6 ? 'good' : score >= 4 ? 'fair' : 'poor';
      const ageYears = getAssetAge(asset);
      quoteTradeIn({ baseValue: purchasePrice, condition, ageYears })
        .then((q) => { if (!cancelled) setTradeInValue(q.value); })
        .catch(() => { if (!cancelled) setTradeInValue(0); });
    }

    const kwh = asset.energyUsageKwh ?? 0;
    if (kwh > 0) {
      quoteEnergySavings({ currentKwhPerYear: kwh })
        .then((q) => { if (!cancelled) setEnergySavingsPerYear(q.annualSavings); })
        .catch(() => { if (!cancelled) setEnergySavingsPerYear(0); });
    }

    return () => { cancelled = true; };
  }, [asset]);

  const model = useMemo(() => (modelId ? findModelById(modelId) : null), [modelId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error || !asset || !model) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen gap-4">
        <AlertCircle className="h-8 w-8 text-muted-foreground" />
        <p className="text-sm text-muted-foreground">{error || t('model.loadError')}</p>
        <Button variant="outline" onClick={() => navigate(`/assets/${id}/upgrade/browse`)}>
          {t('model.backToBrowse')}
        </Button>
      </div>
    );
  }

  const effectivePrice = Math.max(0, model.price - tradeInValue);
  const finalPrice = effectivePrice - (model.rebateEligible ? model.rebateAmount ?? 0 : 0);
  const fiveYearSavings = energySavingsPerYear * 5;
  const imageUrl = getModelImage(model.category ?? model.type, model.brand);

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <section className="border-b bg-background">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate(`/assets/${id}/upgrade/browse`)}
                className="p-2 -ml-2 rounded-lg hover:bg-secondary transition-colors"
              >
                <ArrowLeft className="h-5 w-5" />
              </button>
              <p className="text-sm text-muted-foreground">{t('model.backToBrowseModels')}</p>
            </div>
            <Button
              size="sm"
              className="gap-2"
              onClick={() =>
                navigate(`/assets/${id}/upgrade/checkout`, {
                  state: { model, tradeInValue },
                })
              }
            >
              <ShoppingCart className="h-4 w-4" />
              {t('model.buyNow')} · {formatCurrency(finalPrice)}
            </Button>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-7xl px-6 lg:px-8 py-8">
        {/* Product Grid */}
        <div className="grid lg:grid-cols-2 gap-8 mb-8">
          {/* Left: Image */}
          <div>
            <div className="sticky top-24">
              <div className="relative aspect-square rounded-lg overflow-hidden bg-muted border">
                <img
                  src={imageUrl}
                  alt={`${model.brand} ${model.name}`}
                  className="w-full h-full object-cover"
                />
                {/* Badges */}
                <div className="absolute top-3 left-3 flex flex-col gap-2">
                  {model.tag && (
                    <span className="inline-flex px-2.5 py-1 rounded text-xs font-medium bg-background/90 backdrop-blur">
                      {model.tag}
                    </span>
                  )}
                  {model.energyStarCertified && (
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-xs font-medium bg-background/90 backdrop-blur">
                      <Zap className="h-3 w-3" />
                      {t('model.energyStar')}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Right: Product Info */}
          <div className="space-y-5">
            {/* Brand & Model */}
            <div>
              <p className="text-xs text-muted-foreground mb-2">{model.brand}</p>
              <h1 className="text-2xl font-light mb-1">{model.name}</h1>
              <p className="text-sm text-muted-foreground">{t('model.modelNumberLabel', { number: model.modelNumber })}</p>
            </div>

            {/* Rating */}
            <div className="flex items-center gap-2">
              <div className="flex items-center">
                {[...Array(5)].map((_, i) => (
                  <Star
                    key={i}
                    className={cn(
                      "h-4 w-4",
                      i < Math.floor(model.rating)
                        ? "fill-foreground text-foreground"
                        : "fill-muted text-muted"
                    )}
                  />
                ))}
              </div>
              <span className="text-sm font-medium">{model.rating}</span>
              <span className="text-xs text-muted-foreground">
                {t('model.reviewsCount', { count: model.reviewCount })}
              </span>
            </div>

            {/* Pricing */}
            <div className="rounded-lg border bg-card p-5 space-y-3">
              <h3 className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{t('model.pricing')}</h3>
              
              {model.discount > 0 && (
                <div className="flex items-center justify-between pb-2 border-b text-sm">
                  <span className="text-muted-foreground">{t('model.msrp')}</span>
                  <span className="text-muted-foreground line-through">
                    {formatCurrency(model.price)}
                  </span>
                </div>
              )}
              
              <div className="flex items-center justify-between pb-2 border-b">
                <span className="text-sm">{t('model.salePrice')}</span>
                <span className="text-xl font-light">{formatCurrency(model.price)}</span>
              </div>

              {tradeInValue > 0 && (
                <div className="flex items-center justify-between pb-2 border-b text-sm">
                  <span className="text-muted-foreground">{t('model.tradeInCredit')}</span>
                  <span className="font-medium">−{formatCurrency(tradeInValue)}</span>
                </div>
              )}

              {model.rebateEligible && model.rebateAmount && (
                <div className="flex items-center justify-between pb-2 border-b text-sm">
                  <span className="text-muted-foreground">{t('model.utilityRebate')}</span>
                  <span className="font-medium">−{formatCurrency(model.rebateAmount)}</span>
                </div>
              )}

              <div className="flex items-center justify-between pt-1">
                <span className="text-base font-medium">{t('model.yourPrice')}</span>
                <div className="text-right">
                  <span className="text-2xl font-light block">{formatCurrency(finalPrice)}</span>
                  <span className="text-xs text-muted-foreground">
                    {t('model.monthlyFinancing', { amount: formatCurrency(Math.round(finalPrice / 24)) })}
                  </span>
                </div>
              </div>
            </div>

            {/* Benefits */}
            <div className="space-y-2.5">
              <div className="flex items-center gap-2.5 text-sm text-muted-foreground">
                <Info className="h-4 w-4 flex-shrink-0" />
                <span>{t('model.financingAvailable')}</span>
              </div>
              <div className="flex items-center gap-2.5 text-sm text-muted-foreground">
                <Truck className="h-4 w-4 flex-shrink-0" />
                <span>{t('model.freeDelivery')}</span>
              </div>
              <div className="flex items-center gap-2.5 text-sm text-muted-foreground">
                <Shield className="h-4 w-4 flex-shrink-0" />
                <span>{t('model.warrantyYears', { years: model.warrantyYears })}</span>
              </div>
            </div>

            {/* CTAs */}
            <div className="flex gap-3 pt-3">
              <Button
                size="lg"
                className="flex-1 gap-2"
                onClick={() =>
                  navigate(`/assets/${id}/upgrade/checkout`, {
                    state: { model, tradeInValue },
                  })
                }
              >
                <ShoppingCart className="h-4 w-4" />
                {t('model.proceedToCheckout')}
              </Button>
            </div>
          </div>
        </div>

        {/* Energy Savings */}
        {energySavingsPerYear > 0 && (
          <div className="rounded-lg border bg-card p-6 mb-6">
            <div className="flex items-center gap-3 mb-4">
              <TrendingUp className="h-5 w-5" />
              <div>
                <h3 className="text-lg font-normal">{t('model.energySavings')}</h3>
                <p className="text-xs text-muted-foreground">{t('model.estimatedAnnualSavings')}</p>
              </div>
            </div>
            
            <div className="grid sm:grid-cols-3 gap-6">
              <div>
                <p className="text-xs text-muted-foreground mb-1">{t('model.annualEnergyCost')}</p>
                <p className="text-xl font-light">{t('model.perYear', { amount: formatCurrency(model.annualEnergyCostEst ?? 0) })}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">{t('model.annualSavings')}</p>
                <p className="text-xl font-light">{t('model.perYear', { amount: formatCurrency(energySavingsPerYear) })}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">{t('model.fiveYearSavings')}</p>
                <p className="text-xl font-light">{formatCurrency(fiveYearSavings)}</p>
              </div>
            </div>
          </div>
        )}

        {/* Features */}
        <div className="rounded-lg border bg-card p-6 mb-6">
          <h3 className="text-lg font-normal mb-4">{t('model.keyFeatures')}</h3>
          <ul className="grid sm:grid-cols-2 gap-3">
            {model.features.map((feature: string, i: number) => (
              <li key={i} className="flex items-start gap-2.5">
                <CheckCircle2 className="h-4 w-4 flex-shrink-0 mt-0.5" />
                <span className="text-sm">{feature}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Specifications */}
        <div className="rounded-lg border bg-card p-6">
          <h3 className="text-lg font-normal mb-4">{t('model.specifications')}</h3>
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="flex items-center justify-between py-2.5 px-3 rounded border bg-muted/30">
              <span className="text-sm text-muted-foreground">{t('model.modelNumber')}</span>
              <span className="text-sm font-medium">{model.modelNumber}</span>
            </div>
            <div className="flex items-center justify-between py-2.5 px-3 rounded border bg-muted/30">
              <span className="text-sm text-muted-foreground">{t('model.category')}</span>
              <span className="text-sm font-medium capitalize">{model.category}</span>
            </div>
            <div className="flex items-center justify-between py-2.5 px-3 rounded border bg-muted/30">
              <span className="text-sm text-muted-foreground">{t('model.warranty')}</span>
              <span className="text-sm font-medium">{t('model.warrantyYearLabel', { years: model.warrantyYears })}</span>
            </div>
            <div className="flex items-center justify-between py-2.5 px-3 rounded border bg-muted/30">
              <span className="text-sm text-muted-foreground">{t('model.energyRating')}</span>
              <span className="text-sm font-medium">
                {model.energyStarCertified ? (
                  <span className="flex items-center gap-1.5">
                    <Zap className="h-3.5 w-3.5" />
                    {t('model.energyStar')}
                  </span>
                ) : (
                  t('model.standard')
                )}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
